import { Injectable, effect, inject, signal } from '@angular/core';
import { UsersService } from '@vasta/api-vasta';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, debounceTime, switchMap } from 'rxjs';

export type ThemeMode = 'auto' | 'light' | 'dark';
export type LanguageCode = 'en-GB' | 'it-IT';
export type LanguageSetting = 'auto' | LanguageCode;
export type SchedulingMode = 'weekly' | 'monthly';

export interface UserPreferences {
  themeMode: ThemeMode;
  language: LanguageSetting;
  schedulingMode: SchedulingMode;
  schedulingGridCompact: boolean;
}

const STORAGE_KEY = 'vasta_preferences';

const DEFAULTS: UserPreferences = {
  themeMode: 'auto',
  language: 'en-GB',
  schedulingMode: 'weekly',
  schedulingGridCompact: false,
};

@Injectable({ providedIn: 'root' })
export class PreferencesService {
  private readonly usersService = inject(UsersService);
  private readonly transloco = inject(TranslocoService);
  private readonly _prefs = signal<UserPreferences>(this.loadFromStorage());
  private readonly save$ = new Subject<Partial<UserPreferences>>();

  readonly preferences = this._prefs.asReadonly();

  private readonly systemDark = signal(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  constructor() {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => this.systemDark.set(e.matches));

    effect(() => {
      const mode = this._prefs().themeMode;
      const dark = mode === 'dark' || (mode === 'auto' && this.systemDark());
      document.documentElement.classList.toggle('dark', dark);
    });

    effect(() =>
      this.transloco.setActiveLang(
        this.resolveLanguage(this._prefs().language),
      ),
    );

    this.save$
      .pipe(
        debounceTime(500),
        switchMap((patch) => {
          const { language, ...rest } = patch;
          return this.usersService.updateMyPreferencesUsers({
            apiUpdateUserPreferencesRequestDto: {
              ...rest,
              // `'auto'` is a client-only setting — the API accepts a concrete
              // locale only, so it is resolved against the browser first.
              ...(language === undefined
                ? {}
                : { language: this.resolveLanguage(language) }),
            },
          });
        }),
      )
      .subscribe();
  }

  load(): void {
    this.usersService.getMyPreferencesUsers().subscribe({
      next: (prefs) => {
        this.applyFromApi(prefs as unknown as UserPreferences);
      },
    });
  }

  set<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ): void {
    this._prefs.update((p) => ({ ...p, [key]: value }));
    this.persistToStorage();
    this.save$.next({ [key]: value });
  }

  private resolveLanguage(lang: LanguageSetting): LanguageCode {
    if (lang !== 'auto') return lang;
    const browser = navigator.language;
    return (
      (['en-GB', 'it-IT'] as LanguageCode[]).find((l) =>
        l.toLowerCase().startsWith(browser.split('-')[0].toLowerCase()),
      ) ?? 'en-GB'
    );
  }

  private applyFromApi(prefs: UserPreferences): void {
    const merged: UserPreferences = { ...DEFAULTS, ...prefs };
    this._prefs.set(merged);
    this.persistToStorage();
  }

  private loadFromStorage(): UserPreferences {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw)
        return {
          ...DEFAULTS,
          ...(JSON.parse(raw) as Partial<UserPreferences>),
        };
    } catch {
      // localStorage unavailable (e.g. private browsing) — fall through to defaults
    }
    return { ...DEFAULTS };
  }

  private persistToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._prefs()));
    } catch {
      // localStorage unavailable — skip persistence silently
    }
  }
}
